import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Container, Typography, Stack, Paper, Chip, Avatar, Button,
  IconButton, TextField, MenuItem, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, InputAdornment, LinearProgress, Tab, Tabs,
  Select, FormControl, InputLabel, Snackbar, Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as AssignIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  FlightTakeoff as FlightIcon,
  DirectionsRun as RunningIcon,

  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  AssignmentInd as AssignmentIcon,
  BookmarkAdded as BookmarkIcon,
  EventNote as EventNoteIcon,
  HotelOutlined as HotelIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedquerie } from '../../reduxcomponents/slices/queriesSlice';
import { useNavigate } from 'react-router-dom';
import { fetchAllTrips, assignTrip, fetchExecutiveWorkload, deleteTrip } from '../../api/tripManagementAPI';
import { getPaymentSchedule, upsertPaymentSchedule, updateInstallmentStatus } from '../../api/paymentScheduleAPI';
import { getAllUsers } from '../../api/userAPI';
import usePermissions from '../../hooks/UsePermissions';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (date) => date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtMoney = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const STATUS_CONFIG = {
  Upcoming:  { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  Running:   { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
  Completed: { color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
  Cancelled: { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  'On Hold': { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
};

const PAY_STATUS_CONFIG = {
  Paid:    { color: '#10b981', bg: '#ecfdf5' },
  Partial: { color: '#f59e0b', bg: '#fffbeb' },
  Pending: { color: '#6b7280', bg: '#f9fafb' },
  Overdue: { color: '#ef4444', bg: '#fef2f2' },
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
  return (
    <Chip
      label={status}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 700, fontSize: '0.72rem' }}
    />
  );
};

const PayChip = ({ status }) => {
  const cfg = PAY_STATUS_CONFIG[status] || PAY_STATUS_CONFIG.Pending;
  return (
    <Chip label={status || 'Pending'} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.72rem' }} />
  );
};

// ─── Month picker helper ──────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const THIS_YEAR  = new Date().getFullYear();
const THIS_MONTH = new Date().getMonth(); // 0-indexed

function buildMonthOptions() {
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const m = (THIS_MONTH + i) % 12;
    const y = THIS_YEAR + Math.floor((THIS_MONTH + i) / 12);
    opts.push({ label: `${MONTHS[m]} ${y}`, month: m + 1, year: y });
  }
  return opts;
}

// ─── Payment Schedule Dialog (matches screenshot) ────────────────────────────
const PaymentScheduleDialog = ({ open, onClose, trip, onSaved }) => {
  const queryId = trip?.query_doc_id || null;
  const guestName = trip?.guest_id?.name || '—';

  // Form state (Initial Payment Details)
  const [totalAmount, setTotalAmount]   = useState('');
  const [advancePayment, setAdvancePayment] = useState('');
  const [advanceDate, setAdvanceDate]   = useState('');
  const [advancePaymentLink, setAdvancePaymentLink] = useState('');

  // Installments
  const [installments, setInstallments] = useState([]);

  // Ad-hoc log state
  const [adHocOpen, setAdHocOpen] = useState(false);
  const [adHocAmt, setAdHocAmt]   = useState('');
  const [adHocDate, setAdHocDate] = useState('');
  const [adHocNote, setAdHocNote] = useState('');

  // Add installment inline state
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [newAmt,  setNewAmt]  = useState('');
  const [newDate, setNewDate] = useState('');
  const [newLink, setNewLink] = useState('');
  const [newNote, setNewNote] = useState('');

  const [scheduleId, setScheduleId] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [saving,  setSaving]        = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // ── Load existing schedule ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const advanceSum = (trip?.trip_details?.advance || []).reduce((a, b) => a + b, 0);
    const tripPrice = Number(trip?.trip_details?.price) || Number(trip?.cost) || 0;
    const initialAdv = advanceSum || Number(trip?.advance) || 0;

    if (!queryId) {
      // Reset to blank
      setTotalAmount(tripPrice || '');
      setAdvancePayment(initialAdv || '');
      setAdvanceDate(new Date().toISOString().split('T')[0]);
      setAdvancePaymentLink('');
      setInstallments([]);
      setScheduleId(null);
      return;
    }
    setLoading(true);
    getPaymentSchedule(queryId)
      .then(d => {
        const s = d?.data;
        if (s) {
          setScheduleId(s._id);
          const instSum = (s.installments || []).reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
          const psTotal = Number(s.total_amount) || 0;
          const psAdv = Number(s.advance_payment) || 0;

          if (tripPrice > 0 && psTotal > 0 && Math.abs(psTotal - (psAdv + instSum)) >= 1 && Math.abs(psTotal - tripPrice) >= 1) {
            setTotalAmount(tripPrice);
            setAdvancePayment(initialAdv > 0 ? initialAdv : psAdv);
          } else {
            setTotalAmount(s.total_amount ?? tripPrice ?? '');
            setAdvancePayment(s.advance_payment ?? (initialAdv || ''));
          }

          setAdvanceDate(s.advance_date ? new Date(s.advance_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
          setAdvancePaymentLink(s.advance_payment_link ?? '');
          setInstallments(s.installments || []);
        } else {
          setTotalAmount(tripPrice || '');
          setAdvancePayment(initialAdv || '');
          setAdvanceDate(new Date().toISOString().split('T')[0]);
          setAdvancePaymentLink('');
          setInstallments([]);
          setScheduleId(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, queryId, trip]);

  // ── Computed summary ───────────────────────────────────────────────────────
  const totalAmt      = parseFloat(totalAmount)   || 0;
  const advAmt        = parseFloat(advancePayment) || 0;
  const totalPaid     = installments.filter(i => i.status === 'Paid').reduce((s, i) => s + (i.amount || 0), 0) + (advAmt);
  const totalScheduled = installments.reduce((s, i) => s + (i.amount || 0), 0);
  const unscheduledDue = Math.max(0, totalAmt - advAmt - totalScheduled);

  // ── Save schedule ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!queryId) { setSnack({ open: true, msg: 'No query linked to this trip.', severity: 'error' }); return; }
    
    // If inline add row is open and filled, auto-add it before saving
    let finalInstallments = [...installments];
    if (addRowOpen && newAmt && newDate) {
      const newInst = { amount: parseFloat(newAmt), due_date: newDate, payment_link: newLink, remarks: newNote, status: 'Pending' };
      finalInstallments.push(newInst);
      setInstallments(finalInstallments);
      setNewAmt(''); setNewDate(''); setNewLink(''); setNewNote('');
      setAddRowOpen(false);
    }

    setSaving(true);
    try {
      const result = await upsertPaymentSchedule({
        query_id: queryId,
        total_amount: totalAmt,
        advance_payment: advAmt,
        advance_date: advanceDate,
        advance_payment_link: advancePaymentLink,
        installments: finalInstallments.map(({ _id, ...rest }) => ({ ...rest, ...(scheduleId && _id ? { _id } : {}) })),
      });
      const saved = result?.data;
      if (saved) {
        setScheduleId(saved._id);
        setInstallments(saved.installments || []);
      }
      setSnack({ open: true, msg: 'Payment schedule saved!', severity: 'success' });
      onSaved?.();
      setTimeout(onClose, 800);
    } catch (e) { setSnack({ open: true, msg: 'Failed to save schedule.', severity: 'error' }); }
    setSaving(false);
  };

  // ── Add installment row ────────────────────────────────────────────────────
  const handleAddInstallment = () => {
    if (!newAmt || !newDate) return;
    setInstallments(prev => [
      ...prev,
      { amount: parseFloat(newAmt), due_date: newDate, payment_link: newLink, remarks: newNote, status: 'Pending' }
    ]);
    setNewAmt(''); setNewDate(''); setNewLink(''); setNewNote('');
    setAddRowOpen(false);
  };

  // ── Log ad-hoc payment ─────────────────────────────────────────────────────
  const handleAdHoc = () => {
    if (!adHocAmt) return;
    setInstallments(prev => [
      ...prev,
      { amount: parseFloat(adHocAmt), due_date: adHocDate || new Date().toISOString().split('T')[0], remarks: adHocNote || 'Ad-hoc payment', status: 'Paid' }
    ]);
    setAdHocAmt(''); setAdHocDate(''); setAdHocNote('');
    setAdHocOpen(false);
  };

  // ── Delete installment row ─────────────────────────────────────────────────
  const handleDeleteInstallment = (idx) => {
    setInstallments(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Mark installment paid ──────────────────────────────────────────────────
  const handleMarkPaid = async (idx) => {
    const inst = installments[idx];
    if (inst._id && scheduleId) {
      // persist immediately
      try {
        const r = await updateInstallmentStatus(scheduleId, inst._id, { status: 'Paid', paid_date: new Date() });
        if (r?.data?.installments) { setInstallments(r.data.installments); return; }
      } catch (e) { console.error(e); }
    }
    setInstallments(prev => prev.map((it, i) => i === idx ? { ...it, status: 'Paid', paid_date: new Date() } : it));
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
        {/* ── Header ── */}
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', py: 1.5, px: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={700} fontSize="1rem">
              Payment Schedule: {guestName}
            </Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <>
              {/* ── Section 1: Initial Payment Details ── */}
              <Typography variant="subtitle2" fontWeight={700} color="#1565c0" mb={1.5}>
                Initial Payment Details
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={1.5}>
                <TextField
                  size="small" label="Total Amount" type="number" fullWidth
                  value={totalAmount} onChange={e => setTotalAmount(e.target.value)}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  size="small" label="Advance Payment (First)" type="number" fullWidth
                  value={advancePayment} onChange={e => setAdvancePayment(e.target.value)}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  size="small" label="Advance Date" type="date" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={advanceDate} onChange={e => setAdvanceDate(e.target.value)}
                />
                <TextField
                  size="small" label="Advance Payment Link" fullWidth
                  value={advancePaymentLink} onChange={e => setAdvancePaymentLink(e.target.value)}
                  placeholder="https://..."
                />
              </Stack>

              {/* ── Summary Bar ── */}
              <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 1.5, mb: 2.5, bgcolor: '#fafbfc' }}>
                <Stack direction="row" flexWrap="wrap" gap={3} alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                    <Typography fontWeight={700} color="#1e293b">₹{totalAmt.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Advance</Typography>
                    <Typography fontWeight={700} color="#2e7d32">₹{advAmt.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                    <Typography fontWeight={700} color="#0277bd">₹{totalPaid.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Unscheduled Due</Typography>
                    <Typography fontWeight={700} color={unscheduledDue > 0 ? '#e65100' : '#2e7d32'}>
                      ₹{unscheduledDue.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* ── Section 2: Installment Schedule ── */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="subtitle2" fontWeight={700} color="#1565c0">
                  Installment Schedule
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small" variant="contained" color="success"
                    startIcon={<MoneyIcon />}
                    onClick={() => setAdHocOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, fontSize: '0.78rem' }}
                  >
                    LOG AD-HOC PAYMENT
                  </Button>
                  <Button
                    size="small" variant="outlined"
                    startIcon={<span style={{ fontWeight: 700, fontSize: '1rem' }}>+</span>}
                    onClick={() => setAddRowOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, fontSize: '0.78rem', borderColor: '#1976d2', color: '#1976d2' }}
                  >
                    ADD INSTALLMENT
                  </Button>
                </Stack>
              </Stack>

              {/* Installment Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {['Amount', 'Due Date', 'Payment Link (Opt)', 'Remarks', 'Status', 'Actions'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', py: 1.2 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installments.length === 0 && !addRowOpen ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3.5, color: '#94a3b8', fontSize: '0.83rem' }}>
                          No installments added yet. Click "Add Installment" to schedule due payments.
                        </TableCell>
                      </TableRow>
                    ) : (
                      installments.map((inst, idx) => (
                        <TableRow key={idx}
                          sx={{ bgcolor: inst.status === 'Paid' ? '#f0fdf4' : 'transparent' }}>
                          <TableCell sx={{ fontWeight: 700 }}>₹{Number(inst.amount || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtDate(inst.due_date)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', maxWidth: 120 }}>
                            {inst.payment_link
                              ? <a href={inst.payment_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', wordBreak: 'break-all' }}>{inst.payment_link}</a>
                              : <Typography variant="caption" color="#94a3b8">—</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#475569' }}>{inst.remarks || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={inst.status || 'Pending'}
                              size="small"
                              sx={{
                                fontWeight: 700, fontSize: '0.72rem',
                                bgcolor: inst.status === 'Paid' ? '#dcfce7' : '#f1f5f9',
                                color:   inst.status === 'Paid' ? '#15803d' : '#64748b',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {inst.status !== 'Paid' && (
                                <Tooltip title="Mark as Paid">
                                  <IconButton size="small" color="success" onClick={() => handleMarkPaid(idx)}>
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove">
                                <IconButton size="small" color="error" onClick={() => handleDeleteInstallment(idx)}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}

                    {/* Inline Add Installment Row */}
                    {addRowOpen && (
                      <TableRow sx={{ bgcolor: '#eff6ff' }}>
                        <TableCell>
                          <TextField size="small" type="number" placeholder="Amount" value={newAmt}
                            onChange={e => setNewAmt(e.target.value)} sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="date" value={newDate}
                            onChange={e => setNewDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="Link (opt)" value={newLink}
                            onChange={e => setNewLink(e.target.value)} sx={{ width: 130 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="Remarks" value={newNote}
                            onChange={e => setNewNote(e.target.value)} sx={{ width: 120 }} />
                        </TableCell>
                        <TableCell><Typography variant="caption" color="#64748b">Pending</Typography></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" variant="contained" onClick={handleAddInstallment}
                              disabled={!newAmt || !newDate}
                              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', borderRadius: 1.5 }}>
                              Add
                            </Button>
                            <Button size="small" onClick={() => { setAddRowOpen(false); setNewAmt(''); setNewDate(''); setNewLink(''); setNewNote(''); }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                              Cancel
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>CANCEL</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving || loading}
            startIcon={<PaymentIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }, minWidth: 150 }}
          >
            {saving ? 'Saving…' : 'SAVE SCHEDULE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Ad-hoc Payment Dialog ── */}
      <Dialog open={adHocOpen} onClose={() => setAdHocOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.95rem', borderBottom: '1px solid #e2e8f0', py: 1.5 }}>
          Log Ad-Hoc Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Stack spacing={2}>
            <TextField size="small" label="Amount (₹)" type="number" fullWidth
              value={adHocAmt} onChange={e => setAdHocAmt(e.target.value)} />
            <TextField size="small" label="Payment Date" type="date" fullWidth
              InputLabelProps={{ shrink: true }}
              value={adHocDate} onChange={e => setAdHocDate(e.target.value)} />
            <TextField size="small" label="Note" fullWidth
              value={adHocNote} onChange={e => setAdHocNote(e.target.value)}
              placeholder="e.g. Cash received at office" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAdHocOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!adHocAmt} onClick={handleAdHoc}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}>
            Log Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
};



// ─── Assign Dialog ────────────────────────────────────────────────────────────
const AssignDialog = ({ open, onClose, trip, onAssigned }) => {
  const [executives, setExecutives] = useState([]);
  const [selectedExec, setSelectedExec] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchExecutiveWorkload().then(r => {
      // axios response: r.data is the array
      const list = r?.data || r || [];
      setExecutives(Array.isArray(list) ? list : []);
    }).catch(() => {});
    // pre-fill current assignee
    const current = trip?.allocation?.[0]?.user_id;
    setSelectedExec(current?._id || current || '');
    setReason('');
  }, [open, trip]);

  const handleAssign = async () => {
    if (!selectedExec) return;
    setLoading(true);
    try {
      await assignTrip(trip._id, { executive_id: selectedExec, reason });
      onAssigned?.();
      onClose();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem', pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <AssignmentIcon color="primary" />
          Assign Operation Executive
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Trip: <strong>{trip?.guest_id?.name}</strong> · {fmt(trip?.trip_details?.travel_date)}
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Select Executive</InputLabel>
          <Select value={selectedExec} label="Select Executive" onChange={e => setSelectedExec(e.target.value)}>
            {executives.map(exec => (
              <MenuItem key={exec._id} value={exec._id}>
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <span>{exec.fullName}</span>
                  <Typography variant="caption" color="text.secondary">
                    {exec.total_trips} trips · {exec.running} running
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth size="small" label="Reason (optional)" multiline rows={2}
          value={reason} onChange={e => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disabled={!selectedExec || loading} onClick={handleAssign}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2,
            background: 'linear-gradient(135deg,#1e40af,#3b82f6)' }}>
          {loading ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, icon, gradient, onClick, active }) => (
  <Paper elevation={0} onClick={onClick}
    sx={{
      flex: '1 1 140px', p: 2, borderRadius: 3, cursor: onClick ? 'pointer' : 'default',
      background: active ? gradient : '#fff',
      border: active ? 'none' : '1px solid #e2e8f0',
      transition: 'all 0.2s',
      '&:hover': onClick ? { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' } : {},
    }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ p: 1, borderRadius: 2, bgcolor: active ? 'rgba(255,255,255,0.2)' : '#f1f5f9', display: 'flex' }}>
        {React.cloneElement(icon, { sx: { fontSize: 22, color: active ? '#fff' : '#64748b' } })}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={800} color={active ? '#fff' : '#1e293b'}>{value ?? '—'}</Typography>
        <Typography variant="caption" fontWeight={600} color={active ? 'rgba(255,255,255,0.8)' : '#64748b'}>{label}</Typography>
      </Box>
    </Stack>
  </Paper>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const ConfirmBookingPage = () => {
  const checkPermission = usePermissions();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRole = useSelector(s => s.tokens?.user?.permission);
  const canAssign = ['Admin', 'Admin-a', 'Operation Manager'].includes(userRole);
  const isAdmin = ['Admin', 'Admin-a'].includes(userRole);

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusTab, setStatusTab] = useState('All');
  const [monthFilter, setMonthFilter] = useState(null); // { month, year }
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Dialogs
  const [assignOpen, setAssignOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [activeTrip, setActiveTrip] = useState(null);

  const handleEditOpen = useCallback((rowData) => {
    dispatch(setSelectedquerie({ ...rowData, id: rowData.operation_id }));
    navigate(`/query/view/${rowData.operation_id || rowData._id}`);
  }, [dispatch, navigate]);

  const handleDeleteTrip = async (id) => {
    if (!window.confirm("Are you sure you want to delete this confirmed booking? This action cannot be undone.")) return;
    try {
      await deleteTrip(id);
      setSnack({ open: true, msg: 'Booking deleted successfully', severity: 'success' });
      fetchData();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: e.response?.data?.message || 'Failed to delete booking', severity: 'error' });
    }
  };

  const debounceRef = useRef(null);

  const STATUS_TABS = [
    { label: 'All', value: 'All', icon: <BookmarkIcon fontSize="small" /> },
    { label: 'Upcoming', value: 'Upcoming', icon: <FlightIcon fontSize="small" /> },
    { label: 'Running', value: 'Running', icon: <RunningIcon fontSize="small" /> },
  ];

  const MONTH_OPTIONS = useMemo(() => buildMonthOptions(), []);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusTab,
      };
      if (search) params.search = search;
      if (monthFilter) {
        params.month = monthFilter.month;
        params.year = monthFilter.year;
      } else {
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }

      const res = await fetchAllTrips(params);
      // axios returns response object; backend returns { data: [...], pagination: { total } }
      const payload = res?.data;
      setRows(payload?.data || []);
      setTotal(payload?.pagination?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusTab, search, monthFilter, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(0); }, 450);
  };

  const handleStatusTab = (val) => {
    setStatusTab(val);
    setMonthFilter(null);
    setPage(0);
  };

  const handleMonthFilter = (opt) => {
    if (monthFilter?.month === opt.month && monthFilter?.year === opt.year) {
      setMonthFilter(null);
    } else {
      setMonthFilter(opt);
      setStatusTab('Upcoming');
      setPage(0);
    }
  };

  const handleDateRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    setMonthFilter(null);
    setPage(0);
  };

  const clearAllFilters = () => {
    setSearch(''); setSearchInput('');
    setStatusTab('All'); setMonthFilter(null);
    setDateFrom(''); setDateTo('');
    setPage(0);
  };

  const handleAssignOpen = (trip) => { setActiveTrip(trip); setAssignOpen(true); };
  const handlePayOpen = (trip) => { setActiveTrip(trip); setPayOpen(true); };

  const onAssigned = () => {
    setSnack({ open: true, msg: 'Executive assigned successfully!', severity: 'success' });
    fetchData();
  };

  // KPI counts from loaded rows (approximate — or from server pagination)
  const kpiData = useMemo(() => {
    return {
      all: total,
      upcoming: rows.filter(r => r.trip_status === 'Upcoming').length,
      running: rows.filter(r => r.trip_status === 'Running').length,
    };
  }, [rows, total]);

  const hasActiveFilters = search || monthFilter || dateFrom || dateTo || statusTab !== 'All';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8' }}>
      {/* ── Premium Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg,#1e3a5f 0%,#1e40af 55%,#3b82f6 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 0,
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -80, right: -60,
          width: 280, height: 280, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.06)'
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -50, right: 200,
          width: 150, height: 150, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.04)'
        }
      }}>
        <Container maxWidth={false} disableGutters>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BookmarkIcon fontSize="large" /> Confirm Booking
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', mt: 0.5 }}>
                All confirmed leads with payment scheduling and operation management
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                  bgcolor: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.18)', borderColor: '#fff' },
                  borderRadius: 2, textTransform: 'none', fontWeight: 600
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          {/* KPI cards */}
          <Stack direction="row" flexWrap="wrap" gap={2} mt={3} mb={-2} position="relative" zIndex={1}>
            {[
              { label: 'Total Bookings', value: total, icon: <BookmarkIcon />, gradient: 'linear-gradient(135deg,#1e40af,#3b82f6)', tab: 'All' },
              { label: 'Upcoming', value: kpiData.upcoming, icon: <FlightIcon />, gradient: 'linear-gradient(135deg,#0369a1,#0ea5e9)', tab: 'Upcoming' },
              { label: 'Running', value: kpiData.running, icon: <RunningIcon />, gradient: 'linear-gradient(135deg,#065f46,#10b981)', tab: 'Running' },
            ].map(k => (
              <KPICard key={k.label} label={k.label} value={k.value} icon={k.icon}
                gradient={k.gradient} active={statusTab === k.tab}
                onClick={() => handleStatusTab(k.tab)} />
            ))}
          </Stack>

          {/* Status tabs */}
          <Tabs
            value={statusTab}
            onChange={(_, v) => handleStatusTab(v)}
            sx={{
              mt: 4,
              '& .MuiTabs-indicator': { bgcolor: '#fff', height: 3, borderRadius: '3px 3px 0 0' },
              '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'none', minHeight: 44 },
              '& .Mui-selected': { color: '#fff !important' },
            }}
          >
            {STATUS_TABS.map(t => (
              <Tab key={t.value} value={t.value} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* ── Filters Bar ── */}
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, pt: 2.5, pb: 0 }}>
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search name, email or phone…"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>,
                sx: { borderRadius: 2, bgcolor: '#f8fafc' }
              }}
              sx={{ minWidth: 280 }}
            />

            {/* Date range */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small" type="date" label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={e => handleDateRange(e.target.value, dateTo)}
                sx={{ minWidth: 145, '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
              <Typography color="#94a3b8" fontSize="0.85rem">—</Typography>
              <TextField
                size="small" type="date" label="To"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={e => handleDateRange(dateFrom, e.target.value)}
                sx={{ minWidth: 145, '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Stack>

            {hasActiveFilters && (
              <Button size="small" variant="outlined" color="error" onClick={clearAllFilters}
                sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}>
                Clear Filters
              </Button>
            )}
          </Stack>

          {/* Month chips */}
          <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
            <Typography variant="caption" fontWeight={700} color="#64748b" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, alignSelf: 'center', mr: 0.5 }}>
              Month:
            </Typography>
            {MONTH_OPTIONS.map(opt => {
              const active = monthFilter?.month === opt.month && monthFilter?.year === opt.year;
              return (
                <Chip
                  key={`${opt.month}-${opt.year}`}
                  label={opt.label}
                  size="small"
                  onClick={() => handleMonthFilter(opt)}
                  variant={active ? 'filled' : 'outlined'}
                  color={active ? 'primary' : 'default'}
                  sx={{ fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                />
              );
            })}
          </Stack>
        </Paper>
      </Container>

      {/* ── Data Table ── */}
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 2.5 }}>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
          {loading && <LinearProgress sx={{ borderRadius: '3px 3px 0 0' }} />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Guest', 'Contact', 'Trip Details', 'Travel Date', 'Pax', 'Financials', 'Payment', 'Status', 'Assigned To', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#475569', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Box>
                        <BookmarkIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="h6" color="#94a3b8" fontWeight={600}>No confirmed bookings found</Typography>
                        <Typography variant="body2" color="#cbd5e1" mt={0.5}>
                          {hasActiveFilters ? 'Try adjusting your filters.' : 'Confirmed bookings will appear here.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : rows.map(row => {
                  const guest = row.guest_id;
                  const advance = (row.trip_details?.advance || []).reduce((s, a) => s + a, 0);
                  const price = row.trip_details?.price || 0;
                  const pct = price > 0 ? Math.min(100, Math.round((advance / price) * 100)) : 0;
                  const assignee = row.allocation?.[0]?.user_id;
                  const assigneeName = assignee?.fullName || '—';

                  return (
                    <TableRow key={row._id} hover
                      sx={{ '&:hover': { bgcolor: '#f8faff' }, transition: 'background 0.15s', cursor: 'default' }}>
                      {/* Guest */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ width: 34, height: 34, bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 800, fontSize: 13 }}>
                            {guest?.name?.[0]?.toUpperCase() || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap sx={{ maxWidth: 130 }}>
                              {guest?.name || '—'}
                            </Typography>
                            <Typography variant="caption" color="#94a3b8">
                              {row.booking_ref || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Contact */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="caption" display="block" color="#334155">{guest?.email || '—'}</Typography>
                        <Typography variant="caption" display="block" color="#334155">
                          {guest?.country_code || ''} {guest?.phone || '—'}
                        </Typography>
                      </TableCell>

                      {/* Trip Details */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} color="#334155" noWrap sx={{ maxWidth: 140 }}>
                          {row.trip_details?.starting_point || '—'} → {row.trip_details?.ending_point || '—'}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8">
                          {row.trip_details?.duration ? `${row.trip_details.duration}D` : '—'}
                        </Typography>
                      </TableCell>

                      {/* Travel Date */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CalendarIcon sx={{ fontSize: 14, color: '#64748b' }} />
                          <Typography variant="body2" color="#334155" fontWeight={600} noWrap>
                            {fmt(row.trip_details?.travel_date)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Pax */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="#334155" fontWeight={600}>
                          {(row.trip_details?.number_of_adults || 0) + (row.trip_details?.number_of_children || 0)}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8">
                          {row.trip_details?.number_of_adults || 0}A + {row.trip_details?.number_of_children || 0}C
                        </Typography>
                      </TableCell>

                      {/* Financials */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={700} color="#1e293b">{fmtMoney(price)}</Typography>
                        <Box sx={{ width: 80, mt: 0.5 }}>
                          <Box sx={{ bgcolor: '#e2e8f0', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                            <Box sx={{ width: `${pct}%`, bgcolor: pct >= 100 ? '#10b981' : '#3b82f6', height: '100%', borderRadius: 4, transition: 'width 0.4s' }} />
                          </Box>
                          <Typography variant="caption" color="#64748b">{pct}% paid</Typography>
                        </Box>
                      </TableCell>

                      {/* Payment status */}
                      <TableCell sx={{ py: 1.5 }}>
                        <PayChip status={row.payment?.status} />
                      </TableCell>

                      {/* Trip status */}
                      <TableCell sx={{ py: 1.5 }}>
                        <StatusChip status={row.trip_status} />
                      </TableCell>

                      {/* Assigned To */}
                      <TableCell sx={{ py: 1.5 }}>
                        {assignee ? (
                          <Stack direction="row" alignItems="center" spacing={0.8}>
                            <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700 }}>
                              {assigneeName[0]}
                            </Avatar>
                            <Typography variant="caption" fontWeight={600} color="#334155" noWrap sx={{ maxWidth: 100 }}>
                              {assigneeName}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="#94a3b8">Unassigned</Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Manage Itinerary">
                            <IconButton size="small" onClick={() => handleEditOpen(row)}
                              sx={{ bgcolor: '#eff6ff', color: '#3b82f6', '&:hover': { bgcolor: '#dbeafe' } }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Payment Schedule">
                            <IconButton size="small" onClick={() => handlePayOpen(row)}
                              sx={{ bgcolor: '#f0fdf4', color: '#10b981', '&:hover': { bgcolor: '#dcfce7' } }}>
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canAssign && (
                            <Tooltip title={assignee ? 'Reassign' : 'Assign'}>
                              <IconButton size="small" onClick={() => handleAssignOpen(row)}
                                sx={{ bgcolor: '#eff6ff', color: '#3b82f6', '&:hover': { bgcolor: '#dbeafe' } }}>
                                <AssignIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Delete Booking">
                              <IconButton size="small" onClick={() => handleDeleteTrip(row._id)}
                                sx={{ bgcolor: '#fef2f2', color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </Paper>
      </Container>

      {/* ── Dialogs ── */}
      <AssignDialog open={assignOpen} onClose={() => setAssignOpen(false)} trip={activeTrip} onAssigned={onAssigned} />
      <PaymentScheduleDialog open={payOpen} onClose={() => setPayOpen(false)} trip={activeTrip} onSaved={fetchData} />

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfirmBookingPage;
